import { useState, useEffect, useCallback } from 'react';
import { connectivityService } from '../services/infrastructure/connectivityService';

interface ConnectivityState {
  isOnline: boolean;
  lastOnlineTime: number;
  connectionType?: string;
  effectiveType?: string;
}

interface UseConnectivityReturn {
  isOnline: boolean;
  isOffline: boolean;
  connectionQuality: 'excellent' | 'good' | 'poor' | 'offline';
  isSlowConnection: boolean;
  lastOnlineTime: number;
  queueStatus: { total: number; pending: number; failed: number };
  queueAction: (type: string, data: any, options?: { maxRetries?: number; priority?: 'high' | 'normal' | 'low' }) => string;
  clearQueue: () => void;
  retryPendingActions: () => Promise<void>;
  getTimeSinceLastOnline: () => string;
}

export function useConnectivity(): UseConnectivityReturn {
  const [state, setState] = useState<ConnectivityState>(() => connectivityService.getState());
  const [queueStatus, setQueueStatus] = useState(() => connectivityService.getQueueStatus());

  useEffect(() => {
    // S'abonner aux changements de connectivité
    const unsubscribe = connectivityService.subscribe((newState) => {
      setState(newState);
      setQueueStatus(connectivityService.getQueueStatus());
    });

    // Mettre à jour le statut de la queue périodiquement
    const interval = setInterval(() => {
      setQueueStatus(connectivityService.getQueueStatus());
    }, 5000);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, []);

  const queueAction = useCallback((
    type: string,
    data: any,
    options?: { maxRetries?: number; priority?: 'high' | 'normal' | 'low' }
  ) => {
    const actionId = connectivityService.queueAction(type, data, options);
    setQueueStatus(connectivityService.getQueueStatus());
    return actionId;
  }, []);

  const clearQueue = useCallback(() => {
    connectivityService.clearQueue();
    setQueueStatus(connectivityService.getQueueStatus());
  }, []);

  const retryPendingActions = useCallback(async () => {
    await connectivityService.processPendingActions();
    setQueueStatus(connectivityService.getQueueStatus());
  }, []);

  const getTimeSinceLastOnline = useCallback(() => {
    if (state.isOnline) return 'En ligne';
    
    const timeDiff = Date.now() - state.lastOnlineTime;
    const minutes = Math.floor(timeDiff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `Hors ligne depuis ${days} jour${days > 1 ? 's' : ''}`;
    if (hours > 0) return `Hors ligne depuis ${hours} heure${hours > 1 ? 's' : ''}`;
    if (minutes > 0) return `Hors ligne depuis ${minutes} minute${minutes > 1 ? 's' : ''}`;
    return 'Hors ligne depuis moins d\'une minute';
  }, [state.isOnline, state.lastOnlineTime]);

  return {
    isOnline: state.isOnline,
    isOffline: !state.isOnline,
    connectionQuality: connectivityService.getConnectionQuality(),
    isSlowConnection: connectivityService.isSlowConnection(),
    lastOnlineTime: state.lastOnlineTime,
    queueStatus,
    queueAction,
    clearQueue,
    retryPendingActions,
    getTimeSinceLastOnline
  };
}

export default useConnectivity;
